/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR489: Detached/Stuck/Orphan Capture Run Triage Panel
 * 
 * Admin diagnostics panel with tabs for Stuck, Detached, and Orphan runs.
 * Provides triage actions (mark as timed out) and recovery dry-run.
 */

import React, { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import {
  AlertTriangle, RefreshCw, Clock, Unlink, Search,
  ChevronDown, Copy, Loader2, Shield,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, format } from "date-fns";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CaptureRun {
  id: string;
  status: string;
  adapter_key: string;
  merchant_id: string;
  entrypoint: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  ended_at: string | null;
  heartbeat_at: string | null;
  batch_run_id: string | null;
  capture_attempt_id: string | null;
  merchant_source_id: string | null;
  pages_captured: number | null;
  error_code: string | null;
  error_message: string | null;
  artifact_paths: any;
  flyer_image_capture_params: any;
  diagnostics: any;
  worker_id: string | null;
  age_ms?: number;
}

type TabKey = "stuck" | "detached" | "orphan";

const TAB_CONFIG: Record<TabKey, { label: string; action: string; icon: React.ReactNode; description: string }> = {
  stuck: {
    label: "Stuck",
    action: "list_stuck",
    icon: <Clock className="h-4 w-4" />,
    description: "RUNNING with stale heartbeat or exceeded 30min",
  },
  detached: {
    label: "Detached",
    action: "list_detached",
    icon: <Unlink className="h-4 w-4" />,
    description: "No merchant_source_id, batch_run_id, or capture_attempt_id",
  },
  orphan: {
    label: "Orphan",
    action: "list_orphan",
    icon: <AlertTriangle className="h-4 w-4" />,
    description: "merchant_source_id references a deleted merchant_source",
  },
};

// ─── Hook ────────────────────────────────────────────────────────────────────

function useTriageList(tab: TabKey) {
  return useQuery({
    queryKey: ["capture-triage", tab],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-capture-triage", {
        body: { action: TAB_CONFIG[tab].action, limit: 100 },
      });
      if (error) throw error;
      return (data?.rows ?? []) as CaptureRun[];
    },
    refetchInterval: 30_000,
  });
}

// ─── Run Row ─────────────────────────────────────────────────────────────────

function RunRow({ run, tab, onAction }: { run: CaptureRun; tab: TabKey; onAction: () => void }) {
  const { toast } = useToast();
  const [expanded, setExpanded] = useState(false);
  const [acting, setActing] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<any>(null);

  const copyId = () => {
    navigator.clipboard.writeText(run.id);
    toast({ title: "Copied", description: run.id });
  };

  const handleTriage = async (action: "triage_timeout" | "triage_detach") => {
    setActing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-capture-triage", {
        body: { action, run_id: run.id, reason: `Admin triage from CR489 panel` },
      });
      if (error) throw error;
      toast({
        title: "Triage complete",
        description: `${run.id.slice(0, 8)}… → ${data?.new_status} (${data?.error_code})`,
      });
      onAction();
    } catch (err: any) {
      toast({ title: "Triage failed", description: err.message, variant: "destructive" });
    } finally {
      setActing(false);
    }
  };

  const handleRecoveryDryRun = async () => {
    setActing(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-capture-triage", {
        body: { action: "recovery_dry_run", run_id: run.id },
      });
      if (error) throw error;
      setRecoveryResult(data);
    } catch (err: any) {
      toast({ title: "Recovery check failed", description: err.message, variant: "destructive" });
    } finally {
      setActing(false);
    }
  };

  const statusColor = run.status === "RUNNING" ? "destructive"
    : run.status === "TIMED_OUT" ? "secondary"
    : run.status === "COMPLETE" ? "default"
    : "outline";

  const age = run.started_at
    ? formatDistanceToNow(new Date(run.started_at), { addSuffix: true })
    : "never started";

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="border rounded-lg p-3 mb-2 bg-card">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Badge variant={statusColor as any}>{run.status}</Badge>
            <span className="text-xs font-mono text-muted-foreground truncate">{run.id.slice(0, 12)}…</span>
            <span className="text-xs text-muted-foreground">{run.adapter_key}</span>
            {run.error_code && (
              <Badge variant="outline" className="text-xs">{run.error_code}</Badge>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-xs text-muted-foreground">{age}</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={copyId}>
              <Copy className="h-3 w-3" />
            </Button>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent className="mt-3 space-y-3">
          {/* Details grid */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-muted-foreground">Merchant:</span> {run.merchant_id}</div>
            <div><span className="text-muted-foreground">Pages:</span> {run.pages_captured ?? "—"}</div>
            <div><span className="text-muted-foreground">Batch:</span> {run.batch_run_id?.slice(0, 12) ?? "none"}</div>
            <div><span className="text-muted-foreground">Worker:</span> {run.worker_id ?? "—"}</div>
            <div><span className="text-muted-foreground">Heartbeat:</span> {run.heartbeat_at ? formatDistanceToNow(new Date(run.heartbeat_at), { addSuffix: true }) : "never"}</div>
            <div><span className="text-muted-foreground">Created:</span> {format(new Date(run.created_at), "yyyy-MM-dd HH:mm")}</div>
            {run.error_message && (
              <div className="col-span-2"><span className="text-muted-foreground">Error:</span> <span className="text-destructive">{run.error_message}</span></div>
            )}
            {run.artifact_paths && (
              <div className="col-span-2">
                <span className="text-muted-foreground">Artifacts:</span>{" "}
                {Array.isArray(run.artifact_paths) ? `${run.artifact_paths.length} paths` : "present"}
              </div>
            )}
          </div>

          {/* Diagnostics JSON */}
          {(run.diagnostics || run.flyer_image_capture_params) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Raw diagnostics</summary>
              <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-auto max-h-40">
                {JSON.stringify(run.diagnostics ?? run.flyer_image_capture_params, null, 2)}
              </pre>
            </details>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            {run.status === "RUNNING" && (
              <Button
                size="sm"
                variant="destructive"
                disabled={acting}
                onClick={() => handleTriage("triage_timeout")}
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                Mark Timed Out
              </Button>
            )}
            {(tab === "detached" || tab === "orphan") && run.status !== "TIMED_OUT" && (
              <Button
                size="sm"
                variant="secondary"
                disabled={acting}
                onClick={() => handleTriage("triage_detach")}
              >
                {acting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Unlink className="h-3 w-3 mr-1" />}
                Mark Detached
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={acting}
              onClick={handleRecoveryDryRun}
            >
              {acting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Recovery Check
            </Button>
          </div>

          {/* Recovery result */}
          {recoveryResult && (
            <div className={`p-2 rounded text-xs ${recoveryResult.recoverable ? "bg-green-500/10 border border-green-500/20" : "bg-yellow-500/10 border border-yellow-500/20"}`}>
              <div className="font-medium mb-1">
                {recoveryResult.recoverable ? "✅ Potentially recoverable" : "⚠️ Not recoverable"}
              </div>
              <div>Artifacts: {recoveryResult.artifact_count ?? 0}</div>
              <div>Linkage: {recoveryResult.has_linkage ? "Yes" : "No"}</div>
              {recoveryResult.blocking_reasons?.length > 0 && (
                <div className="mt-1 text-muted-foreground">
                  {recoveryResult.blocking_reasons.map((r: string, i: number) => (
                    <div key={i}>• {r}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

export function CaptureRunTriagePanel() {
  const [activeTab, setActiveTab] = useState<TabKey>("stuck");
  const queryClient = useQueryClient();
  const { data: runs = [], isLoading, error } = useTriageList(activeTab);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["capture-triage"] });
  }, [queryClient]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Capture Run Triage
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={refresh} disabled={isLoading}>
            <RefreshCw className={`h-3 w-3 mr-1 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
          <TabsList className="mb-3">
            {(Object.keys(TAB_CONFIG) as TabKey[]).map((key) => (
              <TabsTrigger key={key} value={key} className="gap-1 text-xs">
                {TAB_CONFIG[key].icon}
                {TAB_CONFIG[key].label}
                {activeTab === key && runs.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1">{runs.length}</Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {(Object.keys(TAB_CONFIG) as TabKey[]).map((key) => (
            <TabsContent key={key} value={key}>
              <p className="text-xs text-muted-foreground mb-3">{TAB_CONFIG[key].description}</p>

              {isLoading && (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
              )}

              {error && (
                <div className="text-sm text-destructive py-4">
                  Failed to load: {(error as Error).message}
                </div>
              )}

              {!isLoading && !error && runs.length === 0 && (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  No {TAB_CONFIG[key].label.toLowerCase()} runs found ✓
                </div>
              )}

              <ScrollArea className="max-h-[500px]">
                {runs.map((run) => (
                  <RunRow key={run.id} run={run} tab={key} onAction={refresh} />
                ))}
              </ScrollArea>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
